import React, { useState, useRef } from 'react';
import { debounce } from 'lodash';
import { Box, Typography, Tooltip, ButtonGroup, Button, TextField, InputAdornment, IconButton } from '@material-ui/core';
import GitHubIcon from '@material-ui/icons/GitHub';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { Table, MarkdownContent, MissingAnnotationEmptyState } from '@backstage/core-components';
import { isGithubSlugSet, GITHUB_PULL_REQUESTS_ANNOTATION } from '../../utils/isGithubSlugSet.esm.js';
import { isRoadieBackstageDefaultFilterSet } from '../../utils/isRoadieBackstageDefaultFilterSet.esm.js';
import { usePullRequests } from '../usePullRequests.esm.js';
import { getStatusIconType } from '../Icons.esm.js';
import { useEntity } from '@backstage/plugin-catalog-react';
import { getHostname } from '../../utils/githubUtils.esm.js';
import { GitHubAuthorizationWrapper } from '@roadiehq/github-auth-utils-react';

const generatedColumns = [
  {
    title: "ID",
    field: "number",
    highlight: true,
    width: "150px",
    render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement("a", { target: "_blank", rel: "noopener noreferrer", href: row.url }, "#", row.number))
  },
  {
    title: "Title",
    field: "title",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, getStatusIconType(row), " ", /* @__PURE__ */ React.createElement(Box, { ml: 1, component: "span" }, row.title))
  },
  {
    title: "Creator",
    field: "creatorNickname",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Box, { fontWeight: "fontWeightBold" }, /* @__PURE__ */ React.createElement(
      "a",
      {
        target: "_blank",
        rel: "noopener noreferrer",
        href: row.creatorProfileLink
      },
      row.creatorNickname
    ))
  },
  {
    title: "Created",
    field: "createdTime",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row.createdTime)
  },
  {
    title: "Last updated",
    field: "updatedTime",
    highlight: true,
    render: (row) => /* @__PURE__ */ React.createElement(Typography, { variant: "body2", noWrap: true }, row.updatedTime)
  }
];
const PullRequestsTableView = ({
  projectName,
  loading,
  pageSize,
  page,
  prData,
  onChangePage,
  onChangePageSize,
  total,
  totalResults,
  StateFilterComponent,
  SearchComponent
}) => {
  return /* @__PURE__ */ React.createElement(
    Table,
    {
      detailPanel: ({ rowData }) => /* @__PURE__ */ React.createElement(Box, { marginLeft: "14px" }, /* @__PURE__ */ React.createElement(
        MarkdownContent,
        {
          content: rowData.body ?? "_No description provided._",
          dialect: "gfm"
        }
      )),
      isLoading: loading,
      options: { paging: true, search: false, pageSize, padding: "dense" },
      totalCount: total,
      page,
      actions: [],
      data: prData ?? [],
      onPageChange: onChangePage,
      onRowsPerPageChange: onChangePageSize,
      title: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
        Box,
        {
          position: "absolute",
          left: "20px",
          top: "17px",
          width: "45%",
          display: "flex",
          alignItems: "center"
        },
        /* @__PURE__ */ React.createElement(GitHubIcon, null),
        /* @__PURE__ */ React.createElement(
          Box,
          {
            ml: 1,
            display: "flex",
            flexDirection: "row",
            alignItems: "baseline"
          },
          /* @__PURE__ */ React.createElement(Typography, { variant: "h6", noWrap: true }, projectName),
          totalResults > 1e3 && /* @__PURE__ */ React.createElement(
            Tooltip,
            {
              title: `Search results are limited to a maximum of ${1e3.toLocaleString()} 
                          items. To refine your results, consider adjusting the search query.`,
              arrow: true
            },
            /* @__PURE__ */ React.createElement(
              Typography,
              {
                variant: "body1",
                noWrap: true,
                color: "primary",
                style: { marginLeft: 10, cursor: "pointer" }
              },
              "Total ",
              totalResults.toLocaleString()
            )
          )
        )
      ), /* @__PURE__ */ React.createElement(
        Box,
        {
          position: "absolute",
          right: "20px",
          top: "15px",
          width: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end"
        },
        StateFilterComponent ? /* @__PURE__ */ React.createElement(StateFilterComponent, null) : /* @__PURE__ */ React.createElement(React.Fragment, null),
        StateFilterComponent && SearchComponent ? /* @__PURE__ */ React.createElement(Box, { mr: 1 }) : /* @__PURE__ */ React.createElement(React.Fragment, null),
        SearchComponent ? /* @__PURE__ */ React.createElement(SearchComponent, null) : /* @__PURE__ */ React.createElement(React.Fragment, null)
      )),
      columns: generatedColumns
    }
  );
};
const PullRequests = (__props) => {
  const { entity } = useEntity();
  const projectName = isGithubSlugSet(entity);
  const defaultFilter = isRoadieBackstageDefaultFilterSet(entity);
  const [owner, repo] = (projectName ?? "/").split("/");
  const [search, setSearch] = useState(`state:open ${defaultFilter}`);
  const setSearchValueDebounced = useRef(debounce(setSearch, 500));
  const onChangePRStatusFilter = (state) => {
    if (state === "all") {
      setSearch(
        (currentSearch) => currentSearch.replace(/state:(open|closed)/g, "").trim()
      );
    } else {
      setSearch(
        (currentSearch) => currentSearch.search(/state:(open|closed)/g) === -1 ? `state:${state} ${currentSearch}` : currentSearch.replace(/state:(open|closed)/g, `state:${state}`)
      );
    }
  };
  const [tableProps, { retry, setPage, setPageSize }] = usePullRequests({
    search,
    owner,
    repo
  });
  const StateFilterComponent = () => /* @__PURE__ */ React.createElement(Box, null, /* @__PURE__ */ React.createElement(ButtonGroup, { color: "primary", "aria-label": "text primary button group" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state:open") !== -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("open")
    },
    "OPEN"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state:closed") !== -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("closed")
    },
    "CLOSED"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      color: search.search("state") === -1 ? "primary" : "default",
      onClick: () => onChangePRStatusFilter("all")
    },
    "ALL"
  )));
  const SearchComponent = () => {
    const [draftSearch, setDraftSearch] = useState(search);
    return /* @__PURE__ */ React.createElement(Box, { width: 500 }, /* @__PURE__ */ React.createElement(
      TextField,
      {
        fullWidth: true,
        onChange: (event) => {
          setDraftSearch(event.target.value);
          setSearchValueDebounced.current(event.target.value);
        },
        placeholder: "Filter",
        value: draftSearch,
        InputProps: {
          startAdornment: /* @__PURE__ */ React.createElement(InputAdornment, { position: "start" }, /* @__PURE__ */ React.createElement(SearchIcon, { fontSize: "small" })),
          endAdornment: /* @__PURE__ */ React.createElement(InputAdornment, { position: "end" }, /* @__PURE__ */ React.createElement(
            IconButton,
            {
              disabled: !draftSearch,
              onClick: () => {
                setDraftSearch("");
                setSearch("");
              }
            },
            /* @__PURE__ */ React.createElement(ClearIcon, { fontSize: "small", "aria-label": "clear" })
          ))
        }
      }
    ));
  };
  return /* @__PURE__ */ React.createElement(
    PullRequestsTableView,
    {
      ...tableProps,
      StateFilterComponent,
      SearchComponent,
      loading: tableProps.loading,
      retry,
      onChangePageSize: setPageSize,
      onChangePage: setPage
    }
  );
};
const PullRequestsTable = (__props) => {
  const { entity } = useEntity();
  const hostname = getHostname(entity);
  const projectName = isGithubSlugSet(entity);
  if (!projectName || projectName === "") {
    return /* @__PURE__ */ React.createElement(
      MissingAnnotationEmptyState,
      {
        annotation: GITHUB_PULL_REQUESTS_ANNOTATION
      }
    );
  }
  return /* @__PURE__ */ React.createElement(GitHubAuthorizationWrapper, { title: "Your Pull Requests", hostname }, /* @__PURE__ */ React.createElement(PullRequests, null));
};

export { PullRequestsTable, PullRequestsTableView };
//# sourceMappingURL=PullRequestsTable.esm.js.map
